import React, { useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import "../../styles/charts/AreaLineChart.scss";

export interface ChartLine {
  dataKey: string;
  name: string;
  stroke: string;
  fill?: string;
  strokeWidth?: number;
  type: "line" | "area";
  dot?: boolean;
  strokeDasharray?: string;
}

interface AreaLineChartProps {
  title: string;
  description?: string;
  data: any[];
  xAxisKey: string;
  lines: ChartLine[];
  showGrid?: boolean;
  height?: number;
  loading?: boolean;
  error?: string;
  showLegend?: boolean;
}

const DEFAULT_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#ec4899",
  "#14b8a6",
];

const wrapText = (text: string, maxLength = 15): string[] => {
  if (text.length <= maxLength) return [text];

  const words = text.split(" ");
  if (words.length === 1) {
    return text.length > maxLength * 2
      ? [text.substring(0, maxLength - 3) + "..."]
      : [
          text.substring(0, Math.ceil(text.length / 2)),
          text.substring(Math.ceil(text.length / 2)),
        ];
  }

  const lines = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxLength) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine =
        word.length > maxLength
          ? word.substring(0, maxLength - 3) + "..."
          : word;
      if (lines.length >= 2) break;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines.slice(0, 3);
};

const CustomXAxisTick = React.memo(({ x, y, payload }: any) => {
  const lines = wrapText(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={0}
          y={i * 14}
          fill="#6b7280"
          textAnchor="middle"
          fontSize={11}
          dominantBaseline="hanging">
          {line}
        </text>
      ))}
    </g>
  );
});

const CustomDot = React.memo(({ cx, cy, stroke, fill }: any) => (
  <circle cx={cx} cy={cy} r={4} fill={fill} stroke={stroke} strokeWidth={2} />
));

const CustomActiveDot = React.memo(({ cx, cy, stroke, fill }: any) => (
  <g>
    <circle
      cx={cx}
      cy={cy}
      r={8}
      fill="rgba(255,255,255,0.3)"
      stroke={stroke}
      strokeWidth={2}
    />
    <circle cx={cx} cy={cy} r={4} fill={fill} stroke={stroke} strokeWidth={2} />
  </g>
));

const AreaLineChart: React.FC<AreaLineChartProps> = ({
  title,
  description,
  data,
  xAxisKey,
  lines,
  showGrid = true,
  height = 400,
  loading = false,
  error,
  showLegend = true,
}) => {
  const processedLines = useMemo(() => {
    const areaLines = lines.filter((line) => line.type === "area");
    const regularLines = lines.filter((line) => line.type === "line");

    const linesWithColors = lines.map((line, index) => ({
      ...line,
      stroke: line.stroke || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      fill:
        line.fill ||
        `${line.stroke || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}20`,
    }));

    return { areaLines, regularLines, linesWithColors };
  }, [lines]);

  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;

      const uniquePayload = payload.filter(
        (entry: any, index: number, self: any[]) =>
          entry.value != null &&
          entry.value !== "" &&
          entry.dataKey &&
          self.findIndex((e) => e.dataKey === entry.dataKey) === index
      );

      if (!uniquePayload.length) return null;

      const sortedPayload = uniquePayload.sort((a: any, b: any) => {
        const aIndex = processedLines.linesWithColors.findIndex(
          (line) => line.dataKey === a.dataKey
        );
        const bIndex = processedLines.linesWithColors.findIndex(
          (line) => line.dataKey === b.dataKey
        );
        return aIndex - bIndex;
      });

      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{label}</p>
          {sortedPayload.map((entry: any, index: number) => {
            const lineConfig = processedLines.linesWithColors.find(
              (line) => line.dataKey === entry.dataKey
            );
            const entryColor =
              entry.color ||
              entry.stroke ||
              lineConfig?.stroke ||
              DEFAULT_COLORS[0];

            return (
              <div key={`${entry.dataKey}-${index}`} className="tooltip-item">
                <div className="tooltip-label">
                  <span
                    className="tooltip-dot"
                    style={{ backgroundColor: entryColor }}
                  />
                  <span className="tooltip-name">
                    {lineConfig?.name || entry.name || entry.dataKey}
                  </span>
                </div>
                <span className="tooltip-value">
                  {typeof entry.value === "number"
                    ? entry.value.toLocaleString()
                    : entry.value}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    [processedLines.linesWithColors]
  );

  const chartElements = useMemo(() => {
    if (!data?.length || !processedLines.linesWithColors.length) return null;

    return (
      <>
        {showGrid && (
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.8} />
        )}

        <XAxis
          dataKey={xAxisKey}
          axisLine={true}
          tickLine={true}
          tick={<CustomXAxisTick />}
          height={50}
          interval={0}
        />

        <YAxis
          axisLine={true}
          tickLine={true}
          tick={{ fontSize: 12, fill: "#6b7280" }}
          width={50}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: "#d1d5db", strokeWidth: 1, strokeDasharray: "5 5" }}
        />

        {processedLines.regularLines.map((line) => (
          <Line
            key={`line-${line.dataKey}`}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.stroke}
            strokeWidth={line.strokeWidth || 2}
            strokeDasharray={line.strokeDasharray}
            dot={line.dot ? <CustomDot /> : false}
            activeDot={line.dot ? <CustomActiveDot /> : false}
          />
        ))}

        {processedLines.areaLines.map((line) => (
          <Line
            key={`area-line-${line.dataKey}`}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.stroke}
            strokeWidth={line.strokeWidth || 2}
            strokeDasharray={line.strokeDasharray}
            fill="none"
            dot={line.dot ? <CustomDot /> : false}
            activeDot={line.dot ? <CustomActiveDot /> : false}
          />
        ))}
      </>
    );
  }, [data, xAxisKey, showGrid, processedLines, CustomTooltip]);

  const chartStates = useMemo(
    () => ({
      loading: (
        <div className="chart-state chart-loading">
          <i className="ti ti-loader-2 spin" />
          <span>Loading chart data...</span>
        </div>
      ),
      error: (
        <div className="chart-state chart-error">
          <i className="ti ti-alert-circle" />
          <span>{error || "Failed to load chart"}</span>
        </div>
      ),
      empty: (
        <div className="chart-state chart-empty">
          <i className="ti ti-chart-area" />
          <span>No data available</span>
        </div>
      ),
    }),
    [error]
  );

  const renderChart = useCallback(() => {
    if (loading) return chartStates.loading;
    if (error) return chartStates.error;
    if (!data?.length) return chartStates.empty;

    return (
      <ComposedChart
        data={data}
        margin={{ top: 0, right: 50, left: 20, bottom: 0 }}>
        {chartElements}
      </ComposedChart>
    );
  }, [loading, error, data, chartElements, chartStates]);

  return (
    <div className="area-line-chart">
      <div className="chart-content" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaLineChart;
